import type { ImportRow } from '@unit/shared';

/**
 * Mapping a spreadsheet's columns onto import fields.
 *
 * Kept free of React and of SheetJS so it can be unit-tested with plain
 * arrays: the parsing library hands us a header row and cell rows, and this
 * module does the rest.
 */

export type ImportField = keyof ImportRow;

export const IMPORT_FIELDS: readonly ImportField[] = [
  'personalNumber',
  'firstName',
  'lastName',
  'email',
  'platoon',
  'team',
  'rank',
  'title',
  'phone',
  'roles',
] as const;

export const REQUIRED_FIELDS: readonly ImportField[] = [
  'personalNumber',
  'firstName',
  'lastName',
  'email',
  'platoon',
  // `team` is optional (story 32): an empty cell puts the soldier directly on the platoon.
] as const;

/**
 * Header names people actually type, in either language. Matched after
 * trimming, lower-casing and stripping quotes/apostrophes, so that דוא"ל and
 * דואל and Email all land on `email`.
 */
const SYNONYMS: Record<ImportField, readonly string[]> = {
  personalNumber: ['מספר אישי', 'מס אישי', 'מ.א', 'מא', 'personal number', 'personalnumber', 'id', 'pn'],
  firstName: ['שם פרטי', 'פרטי', 'first name', 'firstname', 'first', 'given name'],
  lastName: ['שם משפחה', 'משפחה', 'last name', 'lastname', 'last', 'surname', 'family name'],
  email: ['דואל', 'דוא"ל', 'אימייל', 'מייל', 'email', 'e-mail', 'mail'],
  platoon: ['מחלקה', 'platoon'],
  team: ['צוות', 'חוליה', 'team', 'squad'],
  rank: ['דרגה', 'rank'],
  title: ['תפקיד', 'title', 'position', 'role in unit'],
  phone: ['טלפון', 'נייד', 'phone', 'mobile', 'cell'],
  roles: ['הרשאות', 'הרשאה', 'roles', 'role', 'permissions'],
};

const normalise = (header: string) =>
  header
    .trim()
    .toLowerCase()
    .replace(/["'״׳]/g, '')
    .replace(/\s+/g, ' ');

/** Column index per field, or null when nothing in the header row matched. */
export type Mapping = Record<ImportField, number | null>;

export function autoMap(headers: readonly string[]): Mapping {
  const mapping = Object.fromEntries(IMPORT_FIELDS.map((f) => [f, null])) as Mapping;
  const used = new Set<number>();

  for (const field of IMPORT_FIELDS) {
    const wanted = SYNONYMS[field].map(normalise);
    const index = headers.findIndex((h, i) => !used.has(i) && wanted.includes(normalise(h)));
    if (index >= 0) {
      mapping[field] = index;
      used.add(index);
    }
  }
  return mapping;
}

export function missingRequired(mapping: Mapping): ImportField[] {
  return REQUIRED_FIELDS.filter((f) => mapping[f] === null);
}

const cell = (row: readonly unknown[], index: number | null): string => {
  if (index === null) return '';
  const value = row[index];
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

/**
 * Turns cell rows into import rows using the mapping. Blank lines — a
 * trailing empty row is normal in a spreadsheet — are skipped, and the
 * original 1-based spreadsheet line is kept so an error can point at it.
 */
export function toImportRows(
  rows: readonly (readonly unknown[])[],
  mapping: Mapping,
): { row: ImportRow; line: number }[] {
  const out: { row: ImportRow; line: number }[] = [];
  rows.forEach((cells, i) => {
    if (cells.every((c) => c === undefined || c === null || String(c).trim() === '')) return;
    const personalNumber = cell(cells, mapping.personalNumber);
    out.push({
      // +2: 1-based, and the header row is line 1.
      line: i + 2,
      row: {
        email: cell(cells, mapping.email),
        // Excel turns a personal number into a number and can render 9100001
        // as 9100001.0 or 9.1e6; keep only the digits it actually holds.
        personalNumber: /^\d+(\.0+)?$/.test(personalNumber)
          ? personalNumber.replace(/\.0+$/, '')
          : personalNumber,
        firstName: cell(cells, mapping.firstName),
        lastName: cell(cells, mapping.lastName),
        rank: cell(cells, mapping.rank) || null,
        title: cell(cells, mapping.title) || null,
        phone: cell(cells, mapping.phone) || null,
        platoon: cell(cells, mapping.platoon),
        team: cell(cells, mapping.team),
        roles: cell(cells, mapping.roles)
          .split(/[,;/]/)
          .map((r) => r.trim())
          .filter(Boolean),
      },
    });
  });
  return out;
}
